/*
 * The browser's Back closes an open sheet, as it does in a phone's own apps, rather than
 * leaving the page under it. Opening a sheet pushes a copy of the page's history entry, marked as
 * the sheet's; Back pops it, and the pop is the sheet's to close on, so the router never hears of
 * it and the page stays where it was. A sheet closed any other way (its Close, a choice, Esc) takes
 * its entry back off, once the router has had its turn: a choice that navigated has already put
 * a new page above the entry, and that entry is left where it is. So a page that closes a sheet and
 * then opens another page (a Composer's Create) opens it `afterSheetsClose`, once the sheet's entry
 * is off: opened at once, it would leave that entry under the new page, for Back to land on.
 *
 * An entry left behind (by that, or a reload) is the page again, so a step between it and its page
 * shows nothing: that step is skipped, onward in the direction it went. The router may replace the
 * entry under a sheet (a filter chosen in it writes the URL), so the mark is carried over a replace,
 * and a pop the sheet takes puts the page's latest URL and state back where it landed.
 *
 * This is the one place Beacon reaches into `history` around React Router: `pushState` and
 * `replaceState` are wrapped to see what the router writes, and a pop is stopped before the
 * router's own listener runs. That needs the dismissal listening first: a pop is fired at `window`,
 * which Chrome hands to its listeners in the order they were added, capture or not, so `main.tsx`
 * installs it before it creates the router. Heard second, every pop the sheet takes would reach the
 * router too, and take the page back to the URL under the sheet, dropping what was chosen in it.
 */

/** Where a sheet's mark sits in `history.state`. */
const mark = "beacon:sheet"

/** A sheet's mark: which sheet pushed the entry, and how many sheet entries stand on the page. */
type Mark = { id: number; depth: number }

type State = { key?: unknown; [mark]?: Mark } | null | undefined

/** What the dismissal needs from `window`, so a test can hand it a fake. */
export type HistoryHost = {
  history: Pick<History, "state" | "pushState" | "replaceState" | "back" | "forward">
  location: Pick<Location, "href">
  addEventListener(type: "popstate", listener: (event: Event) => void, capture: boolean): void
  setTimeout(handler: () => void, ms: number): unknown
}

type Open = { id: number; depth: number; page: string; close: () => void }

/** Who waits on sheets open as it asked: run once each has closed and its entry is off. */
type Waiting = { sheets: readonly Open[]; then: () => void }

/** An entry as the dismissal saw it last: its state and its URL. */
type Entry = { state: State; href: string }

const markOf = (state: State) => state?.[mark]
const depthOf = (state: State) => markOf(state)?.depth ?? 0

/** The page an entry shows: the router's key for it (a sheet's entry copies it), else its URL. */
const pageOf = ({ state, href }: Entry) =>
  state?.key === undefined ? href : `${String(state.key)} ${href}`

/** `state` with `sheet` as its mark, or with none. */
function marked(state: State, sheet: Mark | undefined): State {
  const { [mark]: _, ...rest } = state ?? {}
  return sheet ? { ...rest, [mark]: sheet } : rest
}

/**
 * Installs the dismissal on `host` and answers `push`: call it as a sheet opens, with how to close
 * it; call what it returns when the sheet closes by anything but Back.
 */
export function backDismissalOn(host: HistoryHost) {
  const { history } = host
  const open: Open[] = []
  const seen = (): Entry => ({ state: history.state as State, href: host.location.href })
  let current = seen()
  /** Pops the dismissal caused itself, by taking a closed sheet's entry back off. */
  let expected = 0
  /** Closed sheets whose entry is yet to be looked at, after the router's turn. */
  let closing = 0
  let next = 0
  const waiting: Waiting[] = []

  /** Runs whoever waits on sheets that have all closed, once no entry is on its way off. */
  function release() {
    if (closing > 0 || expected > 0) return
    const ready = waiting.filter(({ sheets }) => sheets.every((sheet) => !open.includes(sheet)))
    for (const wait of ready) {
      waiting.splice(waiting.indexOf(wait), 1)
      wait.then()
    }
  }

  // What the router writes, seen as it writes it, so a pop knows the entry it left. A replace over
  // a sheet's entry keeps the sheet's mark, or Back would no longer find the sheet.
  const push = history.pushState.bind(history)
  const replace = history.replaceState.bind(history)
  history.pushState = (state, unused, url) => {
    push(state, unused, url)
    current = seen()
  }
  history.replaceState = (state, unused, url) => {
    const kept = markOf(history.state as State)
    replace(kept && !markOf(state as State) ? marked(state as State, kept) : state, unused, url)
    current = seen()
  }

  /** Stops the router hearing a pop, and leaves the landed entry showing what the page showed. */
  function hold(event: Event, left: Entry) {
    event.stopImmediatePropagation()
    const landed = seen()
    if (pageOf(landed) === pageOf(left)) return
    replace(marked(left.state, markOf(landed.state)), "", left.href)
    current = seen()
  }

  host.addEventListener(
    "popstate",
    (event) => {
      const left = current
      current = seen()
      if (expected > 0) {
        expected -= 1
        hold(event, left)
        release()
        return
      }
      // Back from a sheet's entry: close every sheet standing above where it landed.
      const depth = depthOf(current.state)
      const page = pageOf(current)
      const above = open.filter((sheet) => sheet.page !== page || sheet.depth > depth)
      if (above.length > 0) {
        for (const sheet of above.toReversed()) {
          open.splice(open.indexOf(sheet), 1)
          sheet.close()
        }
        // Landing on another page (a jump through the history menu) is the router's to show.
        if (above.every((sheet) => sheet.page === page)) hold(event, left)
        release()
        return
      }
      // A step between a page and an entry a sheet left behind shows nothing: take the next one.
      if (pageOf(left) === page && depthOf(left.state) !== depth) {
        event.stopImmediatePropagation()
        if (depth < depthOf(left.state)) history.back()
        else history.forward()
      }
    },
    // Added before the router's own listener (`installBackDismissal`), which
    // `stopImmediatePropagation` then keeps from running.
    true,
  )

  function pushFor(close: () => void) {
    next += 1
    const sheet: Open = {
      id: next,
      depth: depthOf(history.state as State) + 1,
      page: pageOf(seen()),
      close,
    }
    push(marked(history.state as State, { id: sheet.id, depth: sheet.depth }), "")
    current = seen()
    open.push(sheet)
    return function closed() {
      const at = open.indexOf(sheet)
      if (at < 0) return
      open.splice(at, 1)
      closing += 1
      // After the router's turn: a choice that navigated has put its page above the entry already.
      host.setTimeout(() => {
        closing -= 1
        if (markOf(history.state as State)?.id === sheet.id) {
          expected += 1
          history.back()
        } else release()
      }, 0)
    }
  }

  /**
   * Calls `then` once every sheet open now has closed and its entry is off the history: at once
   * when none is open (the desk opens none). Call it as the page closes a sheet, before it opens
   * another page.
   */
  function afterSheetsClose(then: () => void) {
    waiting.push({ sheets: [...open], then })
    release()
  }

  return Object.assign(pushFor, { afterSheetsClose })
}

let installed: ReturnType<typeof backDismissalOn> | undefined

/** The dismissal on this window. Call it before the router is created, so it hears a pop first. */
export function installBackDismissal() {
  installed ??= backDismissalOn(window)
  return installed
}

/** The dismissal on this window, installed here if the app hasn't installed it already. */
export const backDismissal = installBackDismissal

/**
 * Calls `then` once every sheet open now has closed and its entry is off the history, so a page
 * opened from a sheet as it closes doesn't sit on that entry: Back from it would land there.
 */
export const afterSheetsClose = (then: () => void) => backDismissal().afterSheetsClose(then)
